import {
  createContext,
  useState,
  useEffect,
  useContext,
  type ReactNode,
} from "react";
import Router from "next/router";
import axios from "axios";
import { api } from "../services/apiClient";
import { secureStorage } from "../utils/secureStorage";
import { logger } from "../utils/logger";

interface AuthContextData {
  user: UserProps | undefined;
  isAuthenticated: boolean;
  premium: boolean;
  signIn: (credentials: SignInProps) => Promise<void>;
  signUp: (credentials: SignUpProps) => Promise<void>;
  logoutUser: () => Promise<void>;
  getSavedEmail: () => string | null;
  clearSavedEmail: () => void;
}

interface UserProps {
  id: string;
  name: string;
  email: string;
  endereco: string | null;
  subscriptions?: SubscriptionProps | null;
  trial_status?: string | null;
  trial_ends_at?: string | null;
  is_admin?: boolean;
}

interface SubscriptionProps {
  id: string;
  status: string;
}

type AuthProviderProps = {
  children: ReactNode;
};

interface SignInProps {
  email: string;
  password: string;
  remember?: boolean;
}

interface SignUpProps {
  name: string;
  email: string;
  password: string;
  phone: string;
}

export const AuthContext = createContext({} as AuthContextData);

/** Remove resíduos legados de JWT no storage do browser (pré-Sprint 3). */
function purgeLegacyTokenStorage() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem("@barber.token");
    sessionStorage.removeItem("@barber.token");
    secureStorage.remove("@barber.token");
  } catch {
    // ignore
  }
}

export function signOut() {
  try {
    if (typeof window !== "undefined") {
      secureStorage.set("just_logged_out", "true", { persistent: false });
      purgeLegacyTokenStorage();
    }

    void axios
      .post("/api/auth/logout", {}, { withCredentials: true })
      .catch(() => undefined)
      .finally(() => {
        if (typeof window !== "undefined") {
          window.location.href = "/login?logout=true";
        }
      });
  } catch {
    if (typeof window !== "undefined") {
      window.location.href = "/login?logout=true&error=true";
    }
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserProps | undefined>(undefined);
  const isAuthenticated = !!user;

  function getSavedEmail() {
    if (typeof window !== "undefined") {
      return secureStorage.get("@barber.userEmail");
    }
    return null;
  }

  function clearSavedEmail() {
    if (typeof window !== "undefined") {
      secureStorage.remove("@barber.userEmail");
    }
  }

  useEffect(() => {
    purgeLegacyTokenStorage();

    const isJustLoggedOut =
      typeof window !== "undefined" &&
      (window.location.search.includes("logout=true") ||
        secureStorage.get("just_logged_out") === "true");

    if (isJustLoggedOut) {
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.includes("/login")
      ) {
        secureStorage.remove("just_logged_out");
      }
      return;
    }

    // Em rota pública, /me é opcional (visitante anônimo não tem sessão).
    // Mantemos a chamada para usuários autenticados que abrem links de agendamento.
    api
      .get("/me")
      .then((response) => {
        const {
          id,
          name,
          endereco,
          email,
          subscriptions,
          trial_status,
          trial_ends_at,
          is_admin,
        } = response.data;

        setUser({
          id,
          name,
          email,
          endereco,
          subscriptions,
          trial_status,
          trial_ends_at,
          is_admin,
        });
      })
      .catch((error) => {
        const status = error?.response?.status;

        // 401 sem sessão: estado normal em rota pública; em rota protegida o interceptor trata.
        if (status === 401) {
          return;
        }

        if (status) {
          logger.error("Erro ao verificar sessão", { status });
        }
      });
  }, []);

  async function signIn({ email, password, remember = false }: SignInProps) {
    try {
      const response = await axios.post(
        "/api/auth/login",
        { email, password, remember },
        { withCredentials: true }
      );

      const {
        id,
        name,
        subscriptions,
        endereco,
        trial_status,
        trial_ends_at,
        is_admin,
      } = response.data;

      purgeLegacyTokenStorage();

      if (remember && typeof window !== "undefined") {
        secureStorage.set("@barber.userEmail", email, {
          persistent: true,
          expires: 60 * 60 * 24 * 30,
        });
      } else {
        clearSavedEmail();
      }

      setUser({
        id,
        name,
        email,
        endereco,
        subscriptions,
        trial_status,
        trial_ends_at,
        is_admin,
      });

      const destination = is_admin === true ? "/admin" : "/dashboard";
      if (process.env.NODE_ENV === "production") {
        window.location.href = destination;
      } else {
        Router.push(destination);
      }
    } catch (err: unknown) {
      interface ApiError {
        response?: { status?: number; data?: unknown };
        rateLimitExceeded?: boolean;
      }
      const apiError = err as ApiError;
      if (apiError?.response?.status === 429) {
        apiError.rateLimitExceeded = true;
      }
      throw err;
    }
  }

  async function signUp({ name, email, password, phone }: SignUpProps) {
    try {
      await api.post("/users", {
        name,
        email,
        password,
        phone,
      });
      Router.push("/login");
    } catch {
      // propaga para UI
    }
  }

  async function logoutUser() {
    try {
      purgeLegacyTokenStorage();
      clearSavedEmail();
      setUser(undefined);
      await axios.post("/api/auth/logout", {}, { withCredentials: true });
      window.location.href = "/login?logout=true";
    } catch {
      window.location.href = "/login?logout=true&error=true";
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        signIn,
        signUp,
        premium: user?.subscriptions?.status === "active" || false,
        logoutUser,
        getSavedEmail,
        clearSavedEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
